import React from 'react';
import { useParams } from 'react-router';
import { useState, useEffect } from 'react';
import styles from '../../subject.module.css';
import { subjectApi } from '../../api/subjectApi';
import { moduleApi } from '../../api/moduleApi';
import { Subject, Module } from '../../types/subject';
import Loading from 'components/Loading/Loading';
import { QuizAttempt } from 'features/analytics/components/StudentAnalytics/QuizAttemptsOverview';
import QuizAttemptsOverview from 'features/analytics/components/StudentAnalytics/QuizAttemptsOverview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faCrown, faChartLine, faFire, faClock } from '@fortawesome/free-solid-svg-icons';

interface ModuleDetailProps {
    isCatalogView?: boolean;
}

const ModuleDetail: React.FC<ModuleDetailProps> = ({ isCatalogView = false }) => {
    const { subjectSlug, moduleSlug } = useParams<{ subjectSlug: string; moduleSlug: string }>();

    const [subject, setSubject] = useState<Subject | null>(null);
    const [module, setModule] = useState<Module | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        if (!subjectSlug || !moduleSlug) return;
        setLoading(true);
        try {
            const [subjectRes, moduleRes] = await Promise.all([
                subjectApi.getBySlug(subjectSlug),
                moduleApi.getBySlug(moduleSlug)
            ]);
            setSubject(subjectRes.data);
            setModule(moduleRes.data);
        } catch (error) {
            console.error('Error fetching module detail data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [subjectSlug, moduleSlug]);

    // Dummy attempts data for the current module
    const moduleQuizAttempts: QuizAttempt[] = module?.id === 'algebra' ? [
        {
            id: 102,
            attemptNumber: 1,
            score: 33.3,
            date: 'Oct 22, 2023',
            time: '09:15 AM',
            questions: [
                { id: 'Q1', question: 'Solve for x: 2(x + 3) = 10', yourSelection: 'x = 2', correctAnswer: 'x = 2', status: 'CORRECT' },
                { id: 'Q2', question: 'What is the value of 3x² when x = 4?', yourSelection: '24', correctAnswer: '48', status: 'INCORRECT' },
                { id: 'Q3', question: 'Factorize the expression: x² - 25', yourSelection: '(x - 5)(x - 5)', correctAnswer: '(x - 5)(x + 5)', status: 'INCORRECT' },
            ]
        },
        {
            id: 101,
            attemptNumber: 2,
            score: 100.0,
            date: 'Oct 24, 2023',
            time: '14:20 PM',
            questions: [
                { id: 'Q1', question: 'Solve for x: 2(x + 3) = 10', yourSelection: 'x = 2', correctAnswer: 'x = 2', status: 'CORRECT' },
                { id: 'Q2', question: 'What is the value of 3x² when x = 4?', yourSelection: '48', correctAnswer: '48', status: 'CORRECT' },
                { id: 'Q3', question: 'Factorize the expression: x² - 25', yourSelection: '(x - 5)(x + 5)', correctAnswer: '(x - 5)(x + 5)', status: 'CORRECT' },
            ]
        },
    ] : [];

    const bestScore = moduleQuizAttempts.length > 0
        ? Math.max(...moduleQuizAttempts.map(a => a.score))
        : 0;

    const avgScore = moduleQuizAttempts.length > 0
        ? moduleQuizAttempts.reduce((acc, a) => acc + a.score, 0) / moduleQuizAttempts.length
        : 0;

    const totalAttempts = moduleQuizAttempts.length;

    let age = (module && module.age_group) || 'N/A';
    let level = (module && module.level) || 'All ages';
    
    // if(subjectSlug === 'english-language-learning'){
    //     age = 'All ages'
    //     if (!module || !module.level) {
    //         level = 'Intermediate';
    //     }
    // }

    if (loading) {
        return (
            <div className={styles.container}>
                <Loading message="Loading module details..." />
            </div>
        );
    }

    if (!subject || !module) {
        return (
            <div className={styles.container}>
                <header className={styles.detailHeader}>
                    <h1 className={styles.title}>Module not found</h1>
                </header>
            </div>
        );
    }

    if (isCatalogView) {
        return (
            <div className={styles.catalogDetailContainer}>
                <div className={styles.catalogHeroContainer}>
                    <img
                        src={module.thumbnail_url || 'https://via.placeholder.com/1280x720'}
                        alt={module.name}
                        className={styles.catalogHeroImage}
                    />
                </div>

                <div className={styles.catalogMainContent}>
                    <div className={styles.catalogLeftColumn}>
                        <h1 className={styles.catalogTitle}>{module.name}</h1>
                        <p className={styles.catalogDescription}>
                            {module.long_description}
                        </p>
                    </div>

                    <div className={styles.catalogSidebarBox}>
                        <h3 className={styles.sidebarTitle}>Module Details</h3>
                        <div className={styles.sidebarList}>
                            {/* <div className={styles.sidebarItem}>
                                <span className={styles.sidebarLabel}>Duration:</span>
                                <span className={styles.sidebarValue}>{(module as any).duration || '1 hour 40 minutes'}</span>
                            </div> */}
                            {module.content_company_name && (
                                <div className={styles.sidebarItem}>
                                    <span className={styles.sidebarLabel}>Content Provider:</span>
                                    <span className={styles.sidebarValue}>{module.content_company_name}</span>
                                </div>
                            )}
                            <div className={styles.sidebarItem}>
                                <span className={styles.sidebarLabel}>Module ID:</span>
                                <span className={styles.sidebarValue}>{(module as any).moduleId || module.id}</span>
                            </div>
                            <div className={styles.sidebarItem}>
                                <span className={styles.sidebarLabel}>Age:</span>
                                <span className={styles.sidebarValue}>{age}</span>
                            </div>
                            <div className={styles.sidebarItem}>
                                <span className={styles.sidebarLabel}>Language:</span>
                                <span className={styles.sidebarValue}>{(module as any).language || 'English'}</span>
                            </div>
                            {/* <div className={styles.sidebarItem}>
                                <span className={styles.sidebarLabel}>Category:</span>
                                <span className={styles.sidebarValue}>{subject.name}</span>
                            </div> */}
                            {/* <div className={styles.sidebarItem}>
                                <span className={styles.sidebarLabel}>Mode:</span>
                                <span className={styles.sidebarValue}>Single User</span>
                            </div> */}
                            <div className={styles.sidebarItem}>
                                <span className={styles.sidebarLabel}>Level:</span>
                                <span className={styles.sidebarValue}>{level}</span>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <header className={styles.detailHeader}>
                <h1 className={styles.titleCentered}>{module.name}</h1>
            </header>

            <div className={styles.moduleDetailCard}>
                <img
                    src={module.thumbnail_url || 'https://via.placeholder.com/600x400'}
                    alt={module.name}
                    className={styles.moduleDetailImage}
                />

                <div className={styles.moduleDetailInfo}>
                    <h2 className={styles.descriptionLabel}>Description:</h2>
                    <p className={styles.longDescription}>
                        {module.long_description}
                    </p>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '1rem', flexWrap: 'wrap' }}>
                        <div className={styles.idBadge}>
                            <span className={styles.idIcon}></span>
                            <span>Module ID: {(module as any).moduleId || module.id}</span>
                        </div>
                        {/* {module.content_company_name && (
                            <div className={styles.idBadge}>
                                <span>Provider: {module.content_company_name}</span>
                            </div>
                        )}
                        {module.level && (
                            <div className={styles.idBadge}>
                                <span>Level: {module.level}</span>
                            </div>
                        )} */}
                    </div>
                </div>
            </div>

            {/* KPI Cards Section */}
            {moduleQuizAttempts.length > 0 && (
                <div className={styles.kpiGrid}>
                    <div className={styles.kpiCard}>
                        <div className={styles.kpiContent}>
                            <div>
                                <p className={styles.kpiTitle}>Best Score</p>
                                <h3 className={styles.kpiValue}>
                                    {bestScore.toFixed(1)}
                                    <span className={styles.kpiUnit}>%</span>
                                </h3>
                            </div>
                            <div className={styles.kpiIconWrapper}>
                                <FontAwesomeIcon icon={faCrown} style={{ fontSize: '24px', color: '#fbbf24' }} />
                            </div>
                        </div>
                    </div>

                    <div className={styles.kpiCard}>
                        <div className={styles.kpiContent}>
                            <div>
                                <p className={styles.kpiTitle}>Avg Score</p>
                                <h3 className={styles.kpiValue}>
                                    {avgScore.toFixed(1)}
                                    <span className={styles.kpiUnit}>%</span>
                                </h3>
                            </div>
                            <div className={styles.kpiIconWrapper}>
                                <FontAwesomeIcon icon={faChartLine} style={{ fontSize: '24px', color: '#3b82f6' }} />
                            </div>
                        </div>
                    </div>

                    <div className={styles.kpiCard}>
                        <div className={styles.kpiContent}>
                            <div>
                                <p className={styles.kpiTitle}>Attempts</p>
                                <h3 className={styles.kpiValue}>{totalAttempts}</h3>
                            </div>
                            <div className={styles.kpiIconWrapper}>
                                <FontAwesomeIcon icon={faFire} style={{ fontSize: '24px', color: '#10b981' }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Quiz Attempts Section - Shown only if attempts exist */}
            {moduleQuizAttempts.length > 0 && (
                <div style={{ marginTop: '0px' }}>
                    <h3 style={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <FontAwesomeIcon icon={faTrophy} style={{ color: '#fbbf24' }} />
                        Your Quiz Attempts
                    </h3>
                    <QuizAttemptsOverview attempts={moduleQuizAttempts} />
                </div>
            )}
        </div>
    );
};


export default ModuleDetail;
